import { useState, useCallback, useRef } from "react";
import { useApi } from "../../../../../../api/ApiProvider";
import { GkvBillEditDto, DirectBillingRxDto, TaxierenPrintData } from "../types/interface";
import notificationStore from "../../../../../../stores/Notification/notificationStore";

export interface UseTaxierenReturn {
  /** Data needed by A4/A5 print components. Null when not printing. */
  printData: TaxierenPrintData | null;
  /** Whether a taxieren print is in progress. */
  isPrinting: boolean;
  /** Ref to attach to the hidden print container. */
  printRef: React.RefObject<HTMLDivElement>;
  /** Trigger taxieren print for a specific Rx in a bill. */
  printTaxieren: (rx: DirectBillingRxDto, bill: GkvBillEditDto) => void;
  /** Called by react-to-print's onAfterPrint — updates isTaxieren on backend. */
  onAfterPrint: () => Promise<void>;
  /** Cancel taxieren (set isTaxieren=false) for an Rx. */
  cancelTaxieren: (rxId: string) => Promise<void>;
}

/**
 * Manages the Taxieren print workflow:
 * 1. User clicks "Taxieren" on an Rx row.
 * 2. Sets print data (A4 for dentists, A5 for others).
 * 3. react-to-print triggers browser print dialog.
 * 4. After print: calls `rx.updateIsTaxieren(id, "true")`.
 * 5. Cancel taxieren: calls `rx.updateIsTaxieren(id, "false")`.
 *
 * Returns the updated isTaxieren status so the caller can patch local state.
 */
export function useTaxieren(
  onTaxierenStatusChange: (rxId: string, isTaxieren: boolean) => void,
): UseTaxierenReturn {
  const { rx: rxApi } = useApi();

  const [printData, setPrintData] = useState<TaxierenPrintData | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null!);

  // Track which Rx is currently being taxiert (for onAfterPrint)
  const currentRxIdRef = useRef<string | null>(null);

  const printTaxieren = useCallback((rx: DirectBillingRxDto, bill: GkvBillEditDto) => {
    currentRxIdRef.current = rx.id;

    const isZahnarzt = rx.rxTypeId === "Zahnarzt";
    setPrintData({
      billNumber: bill.transactionNumber,
      rxNumber: rx.rxNumber,
      careProviderIk: bill.account?.ik || "",
      isZahnarzt,
    });
    setIsPrinting(true);
  }, []);

  const onAfterPrint = useCallback(async () => {
    const rxId = currentRxIdRef.current;
    if (!rxId) {
      setIsPrinting(false);
      setPrintData(null);
      return;
    }

    try {
      await rxApi.updateIsTaxieren(rxId, "true");
      onTaxierenStatusChange(rxId, true);
      notificationStore.showMessage("Rezept wurde taxiert", "success");
    } catch (error) {
      console.error("Error updating isTaxieren after print:", error);
      notificationStore.showMessage("Fehler beim Taxieren des Rezepts", "error");
    } finally {
      setIsPrinting(false);
      setPrintData(null);
      currentRxIdRef.current = null;
    }
  }, [rxApi, onTaxierenStatusChange]);

  const cancelTaxieren = useCallback(
    async (rxId: string) => {
      try {
        await rxApi.updateIsTaxieren(rxId, "false");
        onTaxierenStatusChange(rxId, false);
        notificationStore.showMessage("Taxieren erfolgreich storniert", "success");
      } catch (error) {
        console.error("Error cancelling taxieren:", error);
        notificationStore.showMessage("Fehler beim Untaxieren des Rezepts", "error");
        throw error;
      }
    },
    [rxApi, onTaxierenStatusChange],
  );

  return {
    printData,
    isPrinting,
    printRef,
    printTaxieren,
    onAfterPrint,
    cancelTaxieren,
  };
}
